import { Alert, Accordion, AccordionDetails, AccordionSummary, Badge, Box, Button, Card, CardActionArea, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Drawer, FormControl, Grid, IconButton, InputLabel, Link, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScienceIcon from '@mui/icons-material/Science';
import CloseIcon from '@mui/icons-material/Close';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import React, { useEffect, useMemo, useState } from 'react';
import { getApiEndpoint } from '../../config/api';

// Category definitions
const CATEGORIES = [
  'Politics',
  'Economics',
  'Technology',
  'Crypto',
  'Sports',
  'Entertainment',
  'Science',
  'Weather',
  'Other'
] as const;

type Category = typeof CATEGORIES[number];

// Category colors for visual distinction
const CATEGORY_COLORS: Record<Category, string> = {
  Politics: '#e91e63',
  Economics: '#9c27b0',
  Technology: '#2196f3',
  Crypto: '#ff9800',
  Sports: '#4caf50',
  Entertainment: '#00bcd4',
  Science: '#673ab7',
  Weather: '#607d8b',
  Other: '#9e9e9e'
};

// Keywords for category inference
const CATEGORY_KEYWORDS: Record<Category, string[]> = {
  Politics: [
    'election', 'president', 'trump', 'biden', 'congress', 'senate', 'house',
    'democrat', 'republican', 'vote', 'poll', 'governor', 'mayor', 'political',
    'government', 'legislation', 'bill', 'law', 'supreme court', 'impeach',
    'primary', 'nominee', 'cabinet', 'administration', 'executive order',
    'electoral', 'swing state', 'ballot', 'campaign', 'incumbent', 'gop', 'dnc'
  ],
  Economics: [
    'fed', 'federal reserve', 'interest rate', 'inflation', 'gdp', 'recession',
    'stock', 'market', 'economy', 'unemployment', 'jobs', 'trade', 'tariff',
    'fiscal', 'monetary', 'debt', 'deficit', 'treasury', 'bond', 'yield',
    'earnings', 'revenue', 'profit', 'ipo', 'merger', 'acquisition', 's&p',
    'dow', 'nasdaq', 'oil', 'gas', 'commodity', 'housing', 'real estate'
  ],
  Technology: [
    'ai', 'artificial intelligence', 'tech', 'apple', 'google', 'microsoft',
    'amazon', 'meta', 'facebook', 'twitter', 'tesla', 'spacex', 'nvidia',
    'chip', 'semiconductor', 'software', 'hardware', 'cloud', 'cyber',
    'internet', 'social media', 'app', 'startup', 'venture', 'openai', 'gpt',
    'robot', 'automation', 'quantum', '5g', 'satellite', 'launch'
  ],
  Crypto: [
    'bitcoin', 'btc', 'ethereum', 'eth', 'crypto', 'blockchain', 'defi',
    'nft', 'token', 'coin', 'binance', 'coinbase', 'solana', 'cardano',
    'dogecoin', 'altcoin', 'stablecoin', 'usdt', 'usdc', 'mining', 'wallet',
    'exchange', 'dex', 'web3', 'dao'
  ],
  Sports: [
    'nfl', 'nba', 'mlb', 'nhl', 'soccer', 'football', 'basketball', 'baseball',
    'hockey', 'tennis', 'golf', 'boxing', 'ufc', 'mma', 'olympics', 'world cup',
    'championship', 'playoff', 'super bowl', 'finals', 'mvp', 'draft', 'trade',
    'coach', 'player', 'team', 'league', 'espn', 'f1', 'racing', 'athlete'
  ],
  Entertainment: [
    'movie', 'film', 'oscar', 'emmy', 'grammy', 'award', 'box office', 'netflix',
    'disney', 'streaming', 'tv', 'television', 'show', 'series', 'celebrity',
    'music', 'album', 'concert', 'tour', 'hollywood', 'actor', 'actress',
    'director', 'studio', 'release', 'premiere', 'ratings', 'viral', 'tiktok'
  ],
  Science: [
    'nasa', 'space', 'mars', 'moon', 'asteroid', 'climate', 'research',
    'study', 'scientist', 'discovery', 'experiment', 'vaccine', 'drug', 'fda',
    'medicine', 'health', 'disease', 'virus', 'pandemic', 'covid', 'cancer',
    'gene', 'dna', 'biology', 'physics', 'chemistry', 'nobel', 'breakthrough'
  ],
  Weather: [
    'hurricane', 'tornado', 'earthquake', 'flood', 'storm', 'weather',
    'temperature', 'heat', 'cold', 'snow', 'rain', 'drought', 'wildfire',
    'climate change', 'el nino', 'la nina', 'forecast', 'noaa'
  ],
  Other: []
};

// Infer category from market title and subtitle
const inferCategory = (title: string, subtitle?: string, explicitCategory?: string): Category => {
  // If explicit category provided and matches our categories, use it
  if (explicitCategory) {
    const normalized = explicitCategory.toLowerCase();
    for (const cat of CATEGORIES) {
      if (normalized.includes(cat.toLowerCase())) {
        return cat;
      }
    }
  }

  const searchText = `${title} ${subtitle || ''}`.toLowerCase();

  // Check each category's keywords
  let bestMatch: Category = 'Other';
  let bestScore = 0;

  for (const [category, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
    if (category === 'Other') continue;

    let score = 0;
    for (const keyword of keywords) {
      if (searchText.includes(keyword.toLowerCase())) {
        // Longer keywords are more specific, give them more weight
        score += keyword.length;
      }
    }

    if (score > bestScore) {
      bestScore = score;
      bestMatch = category as Category;
    }
  }

  return bestMatch;
};

interface PredictionMarket {
  id: string;
  ticker: string;
  title: string;
  subtitle?: string;
  source: 'Kalshi' | 'Polymarket';
  status: string;
  yes_price: number;
  no_price: number;
  volume: number;
  liquidity?: number;
  url?: string;
  expiresAt?: Date;
  category: Category;
}

// Kalshi Types
interface KalshiMarket {
  ticker: string;
  event_ticker: string;
  title: string;
  subtitle: string;
  status: string;
  yes_bid: number;
  yes_ask: number;
  volume: number;
  open_interest: number;
  liquidity: number;
  close_time?: string;
  expiration_time?: string;
  category?: string;
}

interface KalshiResponse {
  markets: KalshiMarket[];
}

// Polymarket Types
interface PolymarketEvent {
  id: string;
  title: string;
  slug: string;
  endDate?: string;
  markets: {
    id: string;
    question: string;
    outcomePrices: string;
    volume: string;
    liquidity: string;
    endDate?: string;
  }[];
}

type SortOption = 'research' | 'uncertainty' | 'volume' | 'expiring' | 'liquidity';

// Calculate how uncertain a market is (0-100, where 100 = perfectly uncertain at 50/50)
const calculateUncertainty = (yesPrice: number): number => {
  const distanceFrom50 = Math.abs(yesPrice - 50);
  return Math.max(0, 100 - (distanceFrom50 * 2));
};

// Calculate research opportunity score (0-100)
const calculateResearchScore = (market: PredictionMarket): number => {
  const uncertainty = calculateUncertainty(market.yes_price);
  const volumeScore = Math.min(100, Math.log10(Math.max(1, market.volume)) * 20);
  const liquidityScore = Math.min(100, Math.log10(Math.max(1, market.liquidity || 0)) * 25);

  let timeScore = 50;
  if (market.expiresAt) {
    const daysRemaining = (market.expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
    if (daysRemaining < 1) timeScore = 10;
    else if (daysRemaining <= 7) timeScore = 70;
    else if (daysRemaining <= 30) timeScore = 100;
    else if (daysRemaining <= 90) timeScore = 80;
    else timeScore = 50;
  }

  return Math.round(
    (uncertainty * 0.4) +
    (volumeScore * 0.25) +
    (liquidityScore * 0.2) +
    (timeScore * 0.15)
  );
};

// Format time remaining
const formatTimeRemaining = (expiresAt?: Date): string => {
  if (!expiresAt) return 'No expiration';

  const now = new Date();
  const diff = expiresAt.getTime() - now.getTime();

  if (diff < 0) return 'Expired';

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  if (days > 30) {
    const months = Math.floor(days / 30);
    return `${months}mo`;
  }
  if (days > 0) return `${days}d`;
  if (hours > 0) return `${hours}h`;
  return '<1h';
};

// Get color for time remaining
const getTimeColor = (expiresAt?: Date): string => {
  if (!expiresAt) return 'text.secondary';

  const now = new Date();
  const diff = expiresAt.getTime() - now.getTime();
  const days = diff / (1000 * 60 * 60 * 24);

  if (days < 1) return 'error.main';
  if (days < 7) return 'warning.main';
  return 'text.secondary';
};

// Get research score color
const getScoreColor = (score: number): string => {
  if (score >= 70) return '#4caf50';
  if (score >= 50) return '#ff9800';
  return '#9e9e9e';
};

// Get uncertainty badge
const getUncertaintyBadge = (yesPrice: number): { label: string; color: string } | null => {
  const uncertainty = calculateUncertainty(yesPrice);
  if (uncertainty >= 80) return { label: 'HIGH EDGE', color: '#4caf50' };
  if (uncertainty >= 60) return { label: 'UNCERTAIN', color: '#ff9800' };
  return null;
};

interface MarketActionModalProps {
  open: boolean;
  market: PredictionMarket | null;
  onClose: () => void;
  onResearch: (market: PredictionMarket) => void;
}

const MarketActionModal: React.FC<MarketActionModalProps> = ({ open, market, onClose, onResearch }) => {
  if (!market) return null;

  const handleGoToMarket = () => {
    if (market.url) {
      window.open(market.url, '_blank');
    } else if (market.source === 'Kalshi') {
      window.open(`https://kalshi.com/markets/${market.ticker}`, '_blank');
    } else if (market.source === 'Polymarket') {
      window.open(`https://polymarket.com/event/${market.ticker}`, '_blank');
    }
    onClose();
  };

  const handleResearch = () => {
    onResearch(market);
    onClose();
  };

  const researchScore = calculateResearchScore(market);
  const uncertainty = calculateUncertainty(market.yes_price);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Market Actions</span>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={market.category}
              size="small"
              sx={{
                backgroundColor: CATEGORY_COLORS[market.category],
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.7rem'
              }}
            />
            <Chip
              label={`Score: ${researchScore}`}
              size="small"
              sx={{
                backgroundColor: getScoreColor(researchScore),
                color: 'white',
                fontWeight: 'bold'
              }}
            />
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {market.title}
        </Typography>
        {market.subtitle && (
          <Typography variant="body2" color="text.secondary" paragraph>
            {market.subtitle}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1, minWidth: 80 }}>
            <Typography variant="caption" color="text.secondary">Uncertainty</Typography>
            <Typography variant="body1" fontWeight="bold">{uncertainty}%</Typography>
          </Box>
          <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1, minWidth: 80 }}>
            <Typography variant="caption" color="text.secondary">Volume</Typography>
            <Typography variant="body1" fontWeight="bold">${market.volume.toLocaleString()}</Typography>
          </Box>
          <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1, minWidth: 80 }}>
            <Typography variant="caption" color="text.secondary">Expires</Typography>
            <Typography variant="body1" fontWeight="bold">{formatTimeRemaining(market.expiresAt)}</Typography>
          </Box>
          <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1, minWidth: 80 }}>
            <Typography variant="caption" color="text.secondary">Category</Typography>
            <Typography variant="body1" fontWeight="bold">{market.category}</Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>Go to Market:</strong> Open the market page on {market.source} to trade.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Research:</strong> Launch an AI agent to identify 2nd and 3rd order storylines that could impact this market.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleGoToMarket} color="primary">
          Go to Market
        </Button>
        <Button onClick={handleResearch} color="secondary" variant="contained">
          Research
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Research result types
interface ResearchStoryline {
  headline: string;
  domain: string;
  impact_order: 'second' | 'third';
  path_of_impacts: string[];
  exposures?: string[];
  mechanism: string;
  time_horizon: string;
  impact_direction: 'positive' | 'negative' | 'mixed';
  thesis: string;
  supporting_evidence: { url: string; claim: string; excerpt_hint?: string }[];
  importance_score?: number;
}

interface ResearchResult {
  marketId: string;
  title: string;
  runId?: string;
  status: 'pending' | 'completed' | 'failed';
  storylines?: ResearchStoryline[];
  research_notes?: string;
  error?: string;
  createdAt: string;
  completedAt?: string;
}

export const PredictionMarketsDashboardPage: React.FC = () => {
  const [markets, setMarkets] = useState<PredictionMarket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<SortOption>('research');

  const [selectedMarket, setSelectedMarket] = useState<PredictionMarket | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [researchMessage, setResearchMessage] = useState<string | null>(null);

  // Research tracking
  const [researchResults, setResearchResults] = useState<Map<string, ResearchResult>>(new Map());
  const [researchPanelOpen, setResearchPanelOpen] = useState(false);

  // Load existing research results on mount
  useEffect(() => {
    const fetchResearchResults = async () => {
      try {
        const apiEndpoint = getApiEndpoint();
        const res = await fetch(`${apiEndpoint}/prediction-markets/research`);
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            const resultsMap = new Map<string, ResearchResult>();
            data.forEach((result: ResearchResult) => {
              resultsMap.set(result.marketId, result);
            });
            setResearchResults(resultsMap);
            console.log(`Loaded ${data.length} existing research results`);
          }
        }
      } catch (e) {
        console.error('Failed to load existing research results:', e);
      }
    };
    fetchResearchResults();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const apiEndpoint = getApiEndpoint();
        const [kalshiRes, polyRes] = await Promise.allSettled([
          fetch(`${apiEndpoint}/prediction-markets/kalshi?limit=100&status=open`),
          fetch(`${apiEndpoint}/prediction-markets/polymarket?closed=false&limit=50`)
        ]);

        let combinedMarkets: PredictionMarket[] = [];

        // Process Kalshi Data
        if (kalshiRes.status === 'fulfilled' && kalshiRes.value.ok) {
          const data: KalshiResponse = await kalshiRes.value.json();
          const kalshiMarkets = data.markets.map(m => ({
            id: m.ticker,
            ticker: m.ticker,
            title: m.title,
            subtitle: m.subtitle,
            source: 'Kalshi' as const,
            status: m.status,
            yes_price: m.yes_ask,
            no_price: 100 - m.yes_ask,
            volume: m.volume,
            liquidity: m.liquidity,
            url: `https://kalshi.com/markets/${m.event_ticker}`,
            expiresAt: m.expiration_time ? new Date(m.expiration_time) :
                       m.close_time ? new Date(m.close_time) : undefined,
            category: inferCategory(m.title, m.subtitle, m.category)
          }));
          combinedMarkets = [...combinedMarkets, ...kalshiMarkets];
        } else {
          console.error('Failed to fetch Kalshi markets');
        }

        // Process Polymarket Data
        if (polyRes.status === 'fulfilled') {
          if (polyRes.value.ok) {
            try {
              const data: PolymarketEvent[] = await polyRes.value.json();
              const polyMarkets = data.flatMap(event => {
                return event.markets.map(m => {
                  let yesPrice = 0;
                  let noPrice = 0;
                  try {
                    const prices = JSON.parse(m.outcomePrices);
                    if (Array.isArray(prices) && prices.length >= 2) {
                      yesPrice = parseFloat(prices[0]) * 100;
                      noPrice = parseFloat(prices[1]) * 100;
                    }
                  } catch {
                    // ignore parse error
                  }

                  return {
                    id: m.id,
                    ticker: event.slug,
                    title: event.title,
                    subtitle: m.question,
                    source: 'Polymarket' as const,
                    status: 'active',
                    yes_price: yesPrice,
                    no_price: noPrice,
                    volume: parseFloat(m.volume || '0'),
                    liquidity: parseFloat(m.liquidity || '0'),
                    url: `https://polymarket.com/event/${event.slug}`,
                    expiresAt: m.endDate ? new Date(m.endDate) :
                               event.endDate ? new Date(event.endDate) : undefined,
                    category: inferCategory(event.title, m.question)
                  };
                });
              });
              combinedMarkets = [...combinedMarkets, ...polyMarkets];
            } catch (e) {
              console.error('Error parsing Polymarket data:', e);
            }
          } else {
            console.error('Polymarket fetch failed with status:', polyRes.value.status);
          }
        }

        setMarkets(combinedMarkets);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Get category counts for the filter dropdown
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const market of markets) {
      counts[market.category] = (counts[market.category] || 0) + 1;
    }
    return counts;
  }, [markets]);

  const formatPrice = (val: number) => {
    return `${val.toFixed(0)}¢`;
  };

  const formatVolume = (vol: number): string => {
    if (vol >= 1000000) return `$${(vol / 1000000).toFixed(1)}M`;
    if (vol >= 1000) return `$${(vol / 1000).toFixed(0)}K`;
    return `$${vol.toFixed(0)}`;
  };

  const filteredAndSortedMarkets = useMemo(() => {
    let result = markets.filter(market => {
      const matchesSearch = market.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            market.ticker.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            (market.subtitle?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);
      const matchesSource = sourceFilter === 'all' || market.source === sourceFilter;
      const matchesCategory = categoryFilter === 'all' || market.category === categoryFilter;
      return matchesSearch && matchesSource && matchesCategory;
    });

    // Sort based on selected option
    switch (sortBy) {
      case 'research':
        result.sort((a, b) => calculateResearchScore(b) - calculateResearchScore(a));
        break;
      case 'uncertainty':
        result.sort((a, b) => calculateUncertainty(b.yes_price) - calculateUncertainty(a.yes_price));
        break;
      case 'volume':
        result.sort((a, b) => b.volume - a.volume);
        break;
      case 'liquidity':
        result.sort((a, b) => (b.liquidity || 0) - (a.liquidity || 0));
        break;
      case 'expiring':
        result.sort((a, b) => {
          if (!a.expiresAt && !b.expiresAt) return 0;
          if (!a.expiresAt) return 1;
          if (!b.expiresAt) return -1;
          return a.expiresAt.getTime() - b.expiresAt.getTime();
        });
        break;
    }

    return result;
  }, [markets, searchQuery, sourceFilter, categoryFilter, sortBy]);

  const handleCardClick = (market: PredictionMarket) => {
    setSelectedMarket(market);
    setModalOpen(true);
  };

  // Poll for research results
  const pollForResults = async (marketId: string, runId: string) => {
    const apiEndpoint = getApiEndpoint();
    const maxAttempts = 60; // Poll for up to 5 minutes (60 * 5s)
    let attempts = 0;

    const poll = async () => {
      attempts++;
      try {
        const res = await fetch(`${apiEndpoint}/prediction-markets/research?marketId=${encodeURIComponent(marketId)}`);
        if (res.ok) {
          const data = await res.json();
          if (data.status === 'completed' && data.storylines) {
            setResearchResults(prev => {
              const updated = new Map(prev);
              updated.set(marketId, {
                ...data,
                status: 'completed',
              });
              return updated;
            });
            setResearchMessage(`Research complete for "${data.title}"! Found ${data.storylines.length} storylines.`);
            setTimeout(() => setResearchMessage(null), 5000);
            setResearchPanelOpen(true);
            return;
          } else if (data.status === 'failed') {
            setResearchResults(prev => {
              const updated = new Map(prev);
              updated.set(marketId, { ...data, status: 'failed' });
              return updated;
            });
            setResearchMessage(`Research failed for "${data.title}": ${data.error || 'Unknown error'}`);
            setTimeout(() => setResearchMessage(null), 5000);
            return;
          }
        }
      } catch (e) {
        console.error('Polling error:', e);
      }

      if (attempts < maxAttempts) {
        setTimeout(poll, 5000); // Poll every 5 seconds
      } else {
        setResearchMessage(`Research timed out for market ${marketId}. Check back later.`);
        setTimeout(() => setResearchMessage(null), 5000);
      }
    };

    // Start polling after a short delay
    setTimeout(poll, 10000);
  };

  const handleResearch = async (market: PredictionMarket) => {
    setResearchMessage(`Starting research for "${market.title}"...`);
    try {
      const apiEndpoint = getApiEndpoint();
      const res = await fetch(`${apiEndpoint}/prediction-markets/research`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          marketId: market.id,
          title: market.title,
          subtitle: market.subtitle,
          ticker: market.ticker,
          source: market.source,
          category: market.category
        })
      });

      if (res.ok) {
        const data = await res.json();
        console.log('Research response:', data);

        // Handle async response (202 Accepted with runId)
        if (data.runId) {
          // Track pending research
          setResearchResults(prev => {
            const updated = new Map(prev);
            updated.set(market.id, {
              marketId: market.id,
              title: market.title,
              runId: data.runId,
              status: 'pending',
              createdAt: new Date().toISOString(),
            });
            return updated;
          });
          setResearchPanelOpen(true);
          setResearchMessage(`Research queued for "${market.title}". Polling for results...`);
          setTimeout(() => setResearchMessage(null), 5000);

          // Start polling for results
          pollForResults(market.id, data.runId);
        } else if (data.storylines) {
          // Handle sync response
          setResearchResults(prev => {
            const updated = new Map(prev);
            updated.set(market.id, {
              marketId: market.id,
              title: market.title,
              status: 'completed',
              storylines: data.storylines,
              research_notes: data.research_notes,
              createdAt: new Date().toISOString(),
              completedAt: new Date().toISOString(),
            });
            return updated;
          });
          setResearchPanelOpen(true);
          setResearchMessage(`Research complete! Found ${data.storylines.length} storylines.`);
          setTimeout(() => setResearchMessage(null), 5000);
        } else {
          setResearchMessage(`Research started for "${market.title}".`);
          setTimeout(() => setResearchMessage(null), 5000);
        }
      } else {
        const errorData = await res.json().catch(() => ({}));
        setResearchMessage(`Research failed: ${errorData.error || res.statusText}`);
        setTimeout(() => setResearchMessage(null), 5000);
      }
    } catch (e) {
      console.error('Research error:', e);
      setResearchMessage('Error triggering research.');
      setTimeout(() => setResearchMessage(null), 5000);
    }
  };

  // Quick category filter chips
  const handleCategoryChipClick = (category: string) => {
    setCategoryFilter(prev => prev === category ? 'all' : category);
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4">
              Prediction Markets
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Find markets with edge potential for 2nd/3rd order research
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: 150 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Source</InputLabel>
              <Select
                value={sourceFilter}
                label="Source"
                onChange={(e) => setSourceFilter(e.target.value)}
              >
                <MenuItem value="all">All Sources</MenuItem>
                <MenuItem value="Kalshi">Kalshi</MenuItem>
                <MenuItem value="Polymarket">Polymarket</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {CATEGORIES.map(cat => (
                  <MenuItem key={cat} value={cat}>
                    {cat} {categoryCounts[cat] ? `(${categoryCounts[cat]})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value as SortOption)}
              >
                <MenuItem value="research">Best Research Targets</MenuItem>
                <MenuItem value="uncertainty">Most Uncertain</MenuItem>
                <MenuItem value="volume">Highest Volume</MenuItem>
                <MenuItem value="liquidity">Highest Liquidity</MenuItem>
                <MenuItem value="expiring">Expiring Soon</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="View Research Results">
              <Badge badgeContent={researchResults.size} color="primary">
                <IconButton
                  onClick={() => setResearchPanelOpen(true)}
                  sx={{
                    backgroundColor: researchResults.size > 0 ? 'primary.main' : 'action.hover',
                    color: researchResults.size > 0 ? 'white' : 'inherit',
                    '&:hover': { backgroundColor: researchResults.size > 0 ? 'primary.dark' : 'action.selected' }
                  }}
                >
                  <ScienceIcon />
                </IconButton>
              </Badge>
            </Tooltip>
          </Box>
        </Box>

        {/* Category quick filter chips */}
        {!loading && !error && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {CATEGORIES.filter(cat => categoryCounts[cat] > 0).map(cat => (
              <Chip
                key={cat}
                label={`${cat} (${categoryCounts[cat]})`}
                size="small"
                onClick={() => handleCategoryChipClick(cat)}
                sx={{
                  backgroundColor: categoryFilter === cat ? CATEGORY_COLORS[cat] : 'transparent',
                  color: categoryFilter === cat ? 'white' : 'text.primary',
                  border: `1px solid ${CATEGORY_COLORS[cat]}`,
                  fontWeight: categoryFilter === cat ? 'bold' : 'normal',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: categoryFilter === cat ? CATEGORY_COLORS[cat] : `${CATEGORY_COLORS[cat]}20`
                  }
                }}
              />
            ))}
            {categoryFilter !== 'all' && (
              <Chip
                label="Clear"
                size="small"
                onClick={() => setCategoryFilter('all')}
                sx={{
                  backgroundColor: 'action.hover',
                  cursor: 'pointer'
                }}
              />
            )}
          </Box>
        )}

        {/* Stats bar */}
        {!loading && !error && (
          <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              <strong>{filteredAndSortedMarkets.length}</strong> markets
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>{filteredAndSortedMarkets.filter(m => calculateUncertainty(m.yes_price) >= 70).length}</strong> high uncertainty
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>{filteredAndSortedMarkets.filter(m => calculateResearchScore(m) >= 60).length}</strong> good research targets
            </Typography>
          </Box>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {researchMessage && (
          <Alert severity={researchMessage.includes('failed') || researchMessage.includes('Error') ? 'error' : 'info'} sx={{ mb: 2 }}>
            {researchMessage}
          </Alert>
        )}

        {!loading && !error && (
          <Grid container spacing={2}>
            {filteredAndSortedMarkets.map((market) => {
              const researchScore = calculateResearchScore(market);
              const uncertaintyBadge = getUncertaintyBadge(market.yes_price);

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={`${market.source}-${market.id}`}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderLeft: researchScore >= 60 ? `3px solid ${getScoreColor(researchScore)}` : 'none'
                    }}
                  >
                    <CardActionArea
                      onClick={() => handleCardClick(market)}
                      sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start' }}
                    >
                      <CardContent sx={{ width: '100%', p: 2 }}>
                        {/* Header row: source, category, score */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 0.5 }}>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Chip
                              label={market.source}
                              size="small"
                              sx={{
                                fontSize: '0.6rem',
                                height: 18,
                                backgroundColor: market.source === 'Kalshi' ? '#00E676' : '#2979FF',
                                color: market.source === 'Kalshi' ? '#000000' : '#FFFFFF',
                                fontWeight: 'bold'
                              }}
                            />
                            <Chip
                              label={market.category}
                              size="small"
                              sx={{
                                fontSize: '0.55rem',
                                height: 16,
                                backgroundColor: CATEGORY_COLORS[market.category],
                                color: '#FFFFFF',
                                fontWeight: 'bold'
                              }}
                            />
                            {uncertaintyBadge && (
                              <Chip
                                label={uncertaintyBadge.label}
                                size="small"
                                sx={{
                                  fontSize: '0.55rem',
                                  height: 16,
                                  backgroundColor: uncertaintyBadge.color,
                                  color: '#FFFFFF',
                                  fontWeight: 'bold'
                                }}
                              />
                            )}
                          </Box>
                          <Tooltip title="Research Score">
                            <Chip
                              label={researchScore}
                              size="small"
                              sx={{
                                fontSize: '0.7rem',
                                height: 20,
                                minWidth: 32,
                                backgroundColor: getScoreColor(researchScore),
                                color: '#FFFFFF',
                                fontWeight: 'bold'
                              }}
                            />
                          </Tooltip>
                        </Box>

                        {/* Title */}
                        <Typography
                          variant="body1"
                          component="div"
                          sx={{
                            mb: 0.5,
                            lineHeight: 1.3,
                            fontSize: '0.9rem',
                            fontWeight: 'bold',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {market.title}
                        </Typography>

                        {/* Subtitle */}
                        {market.subtitle && market.subtitle !== market.title && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mb: 1.5,
                              fontSize: '0.75rem',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {market.subtitle}
                          </Typography>
                        )}

                        {/* Price row */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>YES</Typography>
                              <Typography variant="body2" color="success.main" fontWeight="bold">
                                {formatPrice(market.yes_price)}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>NO</Typography>
                              <Typography variant="body2" color="error.main" fontWeight="bold">
                                {formatPrice(market.no_price)}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {/* Footer: volume and expiration */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Vol: {formatVolume(market.volume)}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.7rem',
                              color: getTimeColor(market.expiresAt),
                              fontWeight: market.expiresAt && (market.expiresAt.getTime() - Date.now()) < 7 * 24 * 60 * 60 * 1000 ? 'bold' : 'normal'
                            }}
                          >
                            {formatTimeRemaining(market.expiresAt)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      <MarketActionModal
        open={modalOpen}
        market={selectedMarket}
        onClose={() => setModalOpen(false)}
        onResearch={handleResearch}
      />

      {/* Research Results Drawer */}
      <Drawer
        anchor="right"
        open={researchPanelOpen}
        onClose={() => setResearchPanelOpen(false)}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 500, md: 600 }, p: 2 }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            <ScienceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Research Results
          </Typography>
          <IconButton onClick={() => setResearchPanelOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        {researchResults.size === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <ScienceIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="body1">No research yet</Typography>
            <Typography variant="body2">Click "Research" on any market to find 2nd/3rd order storylines</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {Array.from(researchResults.values()).map((result) => (
              <Accordion key={result.marketId} defaultExpanded={result.status === 'completed'}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pr: 2 }}>
                    <Chip
                      label={result.status}
                      size="small"
                      color={result.status === 'completed' ? 'success' : result.status === 'failed' ? 'error' : 'warning'}
                    />
                    <Typography variant="body2" sx={{ flex: 1, fontWeight: 'medium' }} noWrap>
                      {result.title}
                    </Typography>
                    {result.storylines && (
                      <Chip label={`${result.storylines.length} storylines`} size="small" variant="outlined" />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {result.status === 'pending' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">
                        Researching... This may take a few minutes.
                      </Typography>
                    </Box>
                  )}

                  {result.status === 'failed' && (
                    <Alert severity="error">{result.error || 'Research failed'}</Alert>
                  )}

                  {result.status === 'completed' && result.storylines && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {result.storylines.map((storyline, idx) => (
                        <Card key={idx} variant="outlined" sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                            {storyline.impact_direction === 'positive' && <TrendingUpIcon color="success" />}
                            {storyline.impact_direction === 'negative' && <TrendingDownIcon color="error" />}
                            {storyline.impact_direction === 'mixed' && <TrendingFlatIcon color="warning" />}
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                              {storyline.headline}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                            <Chip label={storyline.impact_order} size="small" variant="outlined" />
                            <Chip label={storyline.domain} size="small" variant="outlined" />
                            <Chip label={storyline.time_horizon} size="small" variant="outlined" />
                            <Chip
                              label={storyline.impact_direction}
                              size="small"
                              color={storyline.impact_direction === 'positive' ? 'success' : storyline.impact_direction === 'negative' ? 'error' : 'warning'}
                            />
                          </Box>

                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {storyline.thesis}
                          </Typography>

                          {storyline.path_of_impacts && storyline.path_of_impacts.length > 0 && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                Impact Path:
                              </Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                {storyline.path_of_impacts.join(' → ')}
                              </Typography>
                            </Box>
                          )}

                          {storyline.supporting_evidence && storyline.supporting_evidence.length > 0 && (
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                Sources:
                              </Typography>
                              {storyline.supporting_evidence.slice(0, 3).map((evidence, evidx) => (
                                <Typography key={evidx} variant="caption" display="block" color="text.secondary">
                                  • <Link href={evidence.url} target="_blank" rel="noopener">{evidence.claim}</Link>
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Card>
                      ))}

                      {result.research_notes && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                          <Typography variant="caption">{result.research_notes}</Typography>
                        </Alert>
                      )}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </Drawer>
    </Box>
  );
};
